import React, { ChangeEvent, MouseEvent, PointerEvent, useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CopyButton } from '../../../../../global/copy-button/copy-button';
import { FormField } from '../../../../../global/form/form-field/form-field';
import DefaultButton from '../../../../../global/default-button/default-button';
import { useGlobalEvent } from '../../../../../../infrastructure/hooks/use-global-event';
import { OrganizationLinkViewModel } from './organization-link.viewmodel';

export const OrganizationLinkInput = observer(({ vm, label, onCopy }: { vm: OrganizationLinkViewModel; label: string; onCopy?: ()=>void }): JSX.Element => {
    if (!vm) return null;

    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const inputEnhancerRef = useRef<HTMLDivElement | null>(null);
    const btnRef = useRef<HTMLButtonElement | null>(null);

    const focusInputEnhancer = (): void => inputEnhancerRef?.current.classList.add('focused');
    const blurInputEnhancer = (): void => inputEnhancerRef?.current.classList.remove('focused');

    const focusInput = (e: MouseEvent<HTMLDivElement>): void => {
        e.stopPropagation();
        inputRef?.current.focus();
    };

    const outsideClickHandler = useCallback(
        (e: PointerEvent<HTMLElement>): void => {
            if (
                vm.editModeEnabled
                && !inputRef.current?.contains(e.target as HTMLInputElement)
                && !inputEnhancerRef.current?.contains(e.target as HTMLDivElement)
                && !btnRef.current?.contains(e.target as HTMLButtonElement)
            ) vm.disableEditMode();
        }, [ vm.disableEditMode ]
    );

    useGlobalEvent('click', outsideClickHandler);

    const value = vm.editModeEnabled ? vm.value : vm.toDTO();
    const { valid, changed, validationMessage } = vm.validationState;

    const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
        if (vm.editModeEnabled) vm.updateValue(e?.target?.value ?? '');
    };
    const onEdit = async (): Promise<void> => {
        await vm.enableEditMode();
        inputRef?.current.focus();
    };
    const onCancel = async (e: PointerEvent<HTMLButtonElement>): Promise<void> => {
        e.stopPropagation();

        await vm.disableEditMode();
        vm.resetValue();
    };

    return (
        <FormField label={label}>
            <div className='flex'>
                {
                    vm.editModeEnabled &&
                    <div
                        onClick={focusInput}
                        ref={inputEnhancerRef}
                        className='oe-form-field__control-enhancer truncate-left'
                    >/{vm.linkPrefix}</div>
                }
                <input
                    ref={inputRef}
                    className='oe-form-field__control'
                    disabled={!vm.editModeEnabled}
                    value={value}
                    onChange={onChange}
                    onFocus={focusInputEnhancer}
                    onBlur={blurInputEnhancer}
                />
                <CopyButton
                    copyValue={value}
                    className='m-left-6px'
                    disabled={vm.editModeEnabled || !vm.isValid}
                    onCopy={onCopy}
                />
            </div>
            <div className='flex flex-content-space-between m-right-24px'>
                <span className='oe-form-field__error'>
                    {
                        (!valid && changed && <FormattedMessage {...validationMessage} />)
                        || vm.apiValidationMessage
                    }
                </span>
                {
                    vm.editModeEnabled
                    ?
                    <DefaultButton
                        ref={btnRef}
                        modifier={['slim', 'link-warn']}
                        className='visible-while-hovered'
                        text={intl.formatMessage({id: 'oe.common.cancel'})}
                        onClick={onCancel}
                    />
                    :
                    <DefaultButton
                        ref={btnRef}
                        modifier={['slim', 'link']}
                        className='visible-while-hovered'
                        text={intl.formatMessage({id: 'oe.common.editButton'})}
                        onClick={onEdit}
                    />
                }
            </div>
            { vm.editModeEnabled &&
                <span className='oe-form-field__info'>{intl.formatMessage({ id: 'oe.organization.orgLink.info' })}</span>
            }
    </FormField>
    );
});

export const OrganizationLink = {
    Presenter: props => (<OrganizationLinkInput {...props} label={<FormattedMessage id='oe.event.presentersLink'/>}/>),
    Participant: props => (<OrganizationLinkInput {...props} label={<FormattedMessage id='oe.event.participantLink'/>}/>)
};
